// Calls to the OMS warranty views. REST through GetRequest and PostRequestCall (JSON); the token
// comes from Redux and is passed in. The helpers return { ok, data, code, httpStatus, message,
// details } so a screen never reads the raw envelope. GetRequest and PostRequestCall overwrite the
// envelope's `status` with the HTTP status, so success is 200 or 201.
import GetRequest from "../../../Api/GetRequest";
import PostRequestCall from "../../../Api/PostRequest";
import {
  WARRANTY_PLANS_API_URL,
  WARRANTY_PLAN_DRAFTS_API_URL,
  WARRANTY_PLAN_VERSIONS_API_URL,
  WARRANTY_PLAN_TIERS_API_URL,
  WARRANTY_PLAN_SAMPLE_CYCLES_API_URL,
  WARRANTY_PLAN_PRODUCT_GROUPS_API_URL,
  WARRANTY_HEALTH_API_URL,
  WARRANTY_SUPPORT_SUBSCRIPTIONS_API_URL,
  WARRANTY_SUPPORT_AWAITING_INVOICES_API_URL,
  WARRANTY_SUPPORT_PAYMENTS_API_URL,
  WARRANTY_SUPPORT_PART_ORDERS_API_URL,
  WARRANTY_OPS_API_URL,
  WARRANTY_SUBSCRIPTIONS_API_URL,
  WARRANTY_REFUNDS_API_URL,
  WARRANTY_PARTNER_POLICIES_API_URL,
  WARRANTY_PARTNER_SETTLEMENT_API_URL,
  WARRANTY_REGISTRATIONS_API_URL,
  WARRANTY_REVIEW_QUEUE_API_URL,
  WARRANTY_CLAIMS_API_URL,
  WARRANTY_DOCUMENTS_API_URL,
  WARRANTY_CATALOGUE_COMPONENTS_API_URL,
  WARRANTY_CATALOGUE_MODEL_IMAGES_API_URL,
  WARRANTY_VENDOR_POLICIES_API_URL,
} from "../../Shared/constant";
import { messageForCode } from "./errorMessages";

// Phase 1 is India only.
export const WARRANTY_COUNTRY = "IN";

// The two outcomes of settling a cover refund the provider answered with another amount.
export const SETTLE_OUTCOME = { REFUNDED: "refunded", NOT_REFUNDED: "not_refunded" };

const query = (params) =>
  Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

const part = (value) => encodeURIComponent(value);

// The OMS's own shape-check sentences ("Give a reason.") are plain text made for the person and
// never echo what was sent; they are shown as they come, as text and not too long.
const plainValidationMessage = (code, message) =>
  code === "VALIDATION_FAILED" &&
  typeof message === "string" &&
  message.trim() !== "" &&
  message.length <= 200
    ? message.trim()
    : null;

export const unwrap = (response) => {
  const httpStatus = response?.status;
  if (httpStatus === 200 || httpStatus === 201) {
    return { ok: true, data: response?.data, message: response?.message };
  }
  const code = typeof response?.code === "string" ? response.code : undefined;
  const details =
    response?.data !== null && typeof response?.data === "object" ? response.data : {};
  return {
    ok: false,
    code,
    httpStatus,
    // Our own sentence for the code. The one server text shown is the OMS's plain validation
    // sentence; a Django HTML 404 (an id that is not a UUID) has no code and body text at all, and
    // is the plain "not found" sentence.
    message:
      plainValidationMessage(code, response?.message) || messageForCode(code, httpStatus, details),
    details,
    notFound: httpStatus === 404,
  };
};

const get = async (url, token) => unwrap(await GetRequest(url, token));
const post = async (url, body, token) =>
  unwrap(await PostRequestCall(url, body, token, true));

export const listPlanTiers = (token) =>
  get(`${WARRANTY_PLANS_API_URL}?${query({ country: WARRANTY_COUNTRY })}`, token);

export const listPlanVersions = (token, tier) =>
  get(
    `${WARRANTY_PLAN_TIERS_API_URL}/${part(tier)}/versions?${query({
      country: WARRANTY_COUNTRY,
    })}`,
    token
  );

export const getPlanVersion = (token, versionId) =>
  get(
    `${WARRANTY_PLAN_VERSIONS_API_URL}/${part(versionId)}?${query({
      country: WARRANTY_COUNTRY,
    })}`,
    token
  );

export const comparePlanVersions = (token, from, to) =>
  get(
    `${WARRANTY_PLAN_VERSIONS_API_URL}/compare?${query({
      country: WARRANTY_COUNTRY,
      from,
      to,
    })}`,
    token
  );

export const listSampleCycles = (token, search) =>
  get(
    `${WARRANTY_PLAN_SAMPLE_CYCLES_API_URL}?${query({
      country: WARRANTY_COUNTRY,
      search,
    })}`,
    token
  );

// The OMS product group names an override can be priced for: { items: [{ id, name }] } ordered by
// name, empty for a market other than IN. Since 14 Oct 2026 it also carries the website parent
// models a bike segment can list, `models: [{ slug, name }]` (absent on an older em-platform).
export const listProductGroups = (token) =>
  get(`${WARRANTY_PLAN_PRODUCT_GROUPS_API_URL}?${query({ country: WARRANTY_COUNTRY })}`, token);

export const getPlanDraft = (token, draftId) =>
  get(
    `${WARRANTY_PLAN_DRAFTS_API_URL}/${part(draftId)}?${query({
      country: WARRANTY_COUNTRY,
    })}`,
    token
  );

export const createPlanDraft = (token, body, clientRequestId) =>
  post(
    WARRANTY_PLAN_DRAFTS_API_URL,
    { country: WARRANTY_COUNTRY, ...body, clientRequestId },
    token
  );

export const updatePlanDraft = (token, draftId, revision, content, clientRequestId) =>
  post(
    `${WARRANTY_PLAN_DRAFTS_API_URL}/${part(draftId)}`,
    { country: WARRANTY_COUNTRY, revision, content, clientRequestId },
    token
  );

export const discardPlanDraft = (token, draftId, reason, clientRequestId) =>
  post(
    `${WARRANTY_PLAN_DRAFTS_API_URL}/${part(draftId)}/discard`,
    { country: WARRANTY_COUNTRY, reason, clientRequestId },
    token
  );

// A preview never writes and carries no client request id.
export const previewPlanDraft = (token, draftId, body) =>
  post(
    `${WARRANTY_PLAN_DRAFTS_API_URL}/${part(draftId)}/preview`,
    { country: WARRANTY_COUNTRY, ...body },
    token
  );

export const publishPlanDraft = (
  token,
  draftId,
  revision,
  effectiveFrom,
  reason,
  clientRequestId
) =>
  post(
    `${WARRANTY_PLAN_DRAFTS_API_URL}/${part(draftId)}/publish`,
    { country: WARRANTY_COUNTRY, revision, effectiveFrom, reason, clientRequestId },
    token
  );

export const cancelPlanSchedule = (token, versionId, reason, clientRequestId) =>
  post(
    `${WARRANTY_PLAN_VERSIONS_API_URL}/${part(versionId)}/cancel-schedule`,
    { country: WARRANTY_COUNTRY, reason, clientRequestId },
    token
  );

// ---------------------------------------------------------------------------------------------
// Phase 2: review queue, support, health, exception actions, refunds, partner policies.

const countryQuery = (params = {}) => query({ country: WARRANTY_COUNTRY, ...params });

// Review queue (warranty_review). `group`: live (default on the screen), legacy or all; the answer
// carries `counts: { live, legacy }` for the whole market.
export const listReviewQueue = (token, cursor, group, limit = 20) =>
  get(
    `${WARRANTY_REVIEW_QUEUE_API_URL}?${countryQuery({ state: "open", limit, cursor, group })}`,
    token
  );

// Rejects up to 100 registrations with one reason, each as the single review rejects it. The answer
// is { results: [{ registrationId, outcome, code }], rejected, skipped, failed }.
export const bulkRejectRegistrations = (token, registrationIds, reason, clientRequestId) =>
  post(
    `${WARRANTY_REGISTRATIONS_API_URL}/review/bulk-reject`,
    { country: WARRANTY_COUNTRY, registrationIds, reason, clientRequestId },
    token
  );

export const getReviewDetail = (token, registrationId) =>
  get(
    `${WARRANTY_REGISTRATIONS_API_URL}/${part(registrationId)}/review-detail?${countryQuery()}`,
    token
  );

// Contract version 3 body: `dateDecision` (and `purchaseDate` with "correct") only on an approve.
export const reviewRegistration = (token, registrationId, body, clientRequestId) =>
  post(
    `${WARRANTY_REGISTRATIONS_API_URL}/${part(registrationId)}/review`,
    { country: WARRANTY_COUNTRY, ...body, clientRequestId },
    token
  );

// The existing claim decision: snake case, no country and no client request id (the OMS view
// derives its own idempotency key). `date` carries the purchase date read off the invoice and, to
// replace a verified one, the reason; both are sent only when given.
export const reviewClaim = (token, registrationId, decision, claimantAccountId, note, date = {}) =>
  post(
    `${WARRANTY_CLAIMS_API_URL}/${part(registrationId)}/review`,
    {
      decision,
      claimant_account_id: claimantAccountId,
      ...(note ? { note } : {}),
      ...(date.purchaseDate ? { purchase_date: date.purchaseDate } : {}),
      ...(date.purchaseDateReason ? { purchase_date_reason: date.purchaseDateReason } : {}),
    },
    token
  );

export const getDocumentLink = (token, documentId) =>
  get(`${WARRANTY_DOCUMENTS_API_URL}/${part(documentId)}/download?${countryQuery()}`, token);

// Support (warranty_ops). The search criterion travels in the body, never in the URL.
export const searchSubscriptions = (token, criterion) =>
  post(
    `${WARRANTY_SUPPORT_SUBSCRIPTIONS_API_URL}/search`,
    { country: WARRANTY_COUNTRY, ...criterion },
    token
  );

// The filtered list. `params` are the filter keys of subscriptionList.listQuery: never a phone or a
// frame number, so they may sit in the query string.
export const listSubscriptions = (token, params) =>
  get(`${WARRANTY_SUPPORT_SUBSCRIPTIONS_API_URL}?${countryQuery(params)}`, token);

// Registrations waiting for the rider's invoice. `params` are the filter keys of
// awaitingInvoiceList.listQuery: never a phone or a frame number, so they may sit in the query string.
export const listAwaitingInvoices = (token, params) =>
  get(`${WARRANTY_SUPPORT_AWAITING_INVOICES_API_URL}?${countryQuery(params)}`, token);

export const getSubscription = (token, subscriptionId) =>
  get(`${WARRANTY_SUPPORT_SUBSCRIPTIONS_API_URL}/${part(subscriptionId)}?${countryQuery()}`, token);

export const listSubscriptionPayments = (token, subscriptionId, cursor) =>
  get(
    `${WARRANTY_SUPPORT_SUBSCRIPTIONS_API_URL}/${part(subscriptionId)}/payments?${countryQuery({
      cursor,
    })}`,
    token
  );

export const listSubscriptionEvents = (token, subscriptionId, cursor) =>
  get(
    `${WARRANTY_SUPPORT_SUBSCRIPTIONS_API_URL}/${part(subscriptionId)}/events?${countryQuery({
      cursor,
    })}`,
    token
  );

export const getReceiptLink = (token, paymentId) =>
  get(`${WARRANTY_SUPPORT_PAYMENTS_API_URL}/${part(paymentId)}/receipt-link?${countryQuery()}`, token);

// Chat orders (warranty_ops or warranty_view): the battery and charger orders the support bot placed.
// `params` are the filter keys of chatOrderList.listQuery (status, from, to, cursor, limit): never
// personal, so they may sit in the query string. Answers { items, nextCursor }.
export const listChatOrders = (token, params) =>
  get(`${WARRANTY_SUPPORT_PART_ORDERS_API_URL}?${countryQuery(params)}`, token);

// One chat order with its event history: the order's fields plus `events: [{ kind, at, actor, details }]`.
export const getChatOrder = (token, partOrderId) =>
  get(`${WARRANTY_SUPPORT_PART_ORDERS_API_URL}/${part(partOrderId)}?${countryQuery()}`, token);

// All warranties (warranty_review). One POST for the list and the search, so the search text (it
// can be a phone) travels in the body and never in a URL. `body` is allWarrantiesList.searchBody:
// { q, status, source, plan, from, to, cursor, limit }, set keys only. Answers { items, nextCursor },
// latest registration first.
export const searchAllWarranties = (token, body) =>
  post(`${WARRANTY_REGISTRATIONS_API_URL}/search`, { country: WARRANTY_COUNTRY, ...body }, token);

// One registration's journey: { registration, stages, events, links, nextEventsCursor }. With
// `eventsCursor` (the previous answer's nextEventsCursor) the events are the next, older 50.
export const getWarrantyJourney = (token, registrationId, eventsCursor) =>
  get(
    `${WARRANTY_REGISTRATIONS_API_URL}/${part(registrationId)}/journey?${countryQuery({
      eventsCursor,
    })}`,
    token
  );

// Health and exception lists (warranty_ops)
export const getHealth = (token, window) =>
  get(`${WARRANTY_HEALTH_API_URL}?${countryQuery({ window })}`, token);

export const listExceptions = (token, signal, { window, cursor } = {}) =>
  get(
    `${WARRANTY_HEALTH_API_URL}/${part(signal)}/exceptions?${countryQuery({ window, cursor })}`,
    token
  );

// The three resolving actions of the first three signals. Each takes the id of the list item.
const opsAction = (path) => (token, id, reason, clientRequestId) =>
  post(
    `${WARRANTY_OPS_API_URL}/${path(part(id))}`,
    { country: WARRANTY_COUNTRY, reason, clientRequestId },
    token
  );
export const recheckPayment = opsAction((id) => `payments/${id}/recheck`);
export const retryRefund = opsAction((id) => `payments/${id}/retry-refund`);
export const retryOutboxJob = opsAction((id) => `outbox/${id}/retry`);

// Partner policy actions (warranty_ops, behind the partner flag)
const partnerAction = (suffix) => (token, id, reason, clientRequestId) =>
  post(
    `${WARRANTY_PARTNER_POLICIES_API_URL}/${part(id)}/${suffix}`,
    { country: WARRANTY_COUNTRY, reason, clientRequestId },
    token
  );
export const retryPartnerPolicy = partnerAction("retry");
export const askPartnerAgain = partnerAction("fetch");
export const refundPartnerCover = partnerAction("refund-cover");

// Settle a cover refund the provider answered with another amount, after staff checked the
// provider dashboard. `outcome` is "refunded" (with the amount the provider reports, in paise) or
// "not_refunded" (no amount is sent).
export const settlePartnerCoverRefund = (token, id, { outcome, providerAmountMinor }, reason, clientRequestId) =>
  post(
    `${WARRANTY_PARTNER_POLICIES_API_URL}/${part(id)}/settle-cover-refund`,
    {
      country: WARRANTY_COUNTRY,
      reason,
      outcome,
      ...(outcome === SETTLE_OUTCOME.REFUNDED ? { providerAmountMinor } : {}),
      clientRequestId,
    },
    token
  );

export const getPartnerPolicy = (token, policyId) =>
  get(`${WARRANTY_PARTNER_POLICIES_API_URL}/${part(policyId)}?${countryQuery()}`, token);

// One function per HealthAction, so a list row only needs the action name its item carries.
export const ACTION_CALLS = {
  recheck_payment: recheckPayment,
  retry_refund: retryRefund,
  retry_outbox: retryOutboxJob,
  retry_partner_policy: retryPartnerPolicy,
  ask_partner_again: askPartnerAgain,
  refund_cover: refundPartnerCover,
};

// Refunds (warranty_ops)
export const getRefundCheck = (token, subscriptionId) =>
  get(`${WARRANTY_SUBSCRIPTIONS_API_URL}/${part(subscriptionId)}/refund-check?${countryQuery()}`, token);

export const refundSubscription = (token, subscriptionId, reasonCode, clientRequestId) =>
  post(
    `${WARRANTY_SUBSCRIPTIONS_API_URL}/${part(subscriptionId)}/refund`,
    { reasonCode, country: WARRANTY_COUNTRY, clientRequestId },
    token
  );

export const listRecentRefunds = (token, cursor) =>
  get(`${WARRANTY_REFUNDS_API_URL}?${countryQuery({ cursor })}`, token);

// Monthly partner settlement (warranty_ops). `month` is YYYY-MM in the market's time zone.
export const SETTLEMENT_PAGE_SIZE = 50;

export const listSettlementPartners = (token) =>
  get(`${WARRANTY_PARTNER_SETTLEMENT_API_URL}/partners?${countryQuery()}`, token);

export const getPartnerSettlement = (token, { partnerKey, month, cursor }) =>
  get(
    `${WARRANTY_PARTNER_SETTLEMENT_API_URL}?${countryQuery({
      partnerKey,
      month,
      cursor,
      limit: SETTLEMENT_PAGE_SIZE,
    })}`,
    token
  );

// The file name the OMS sends (PS-IN-acko-202609.csv), else one made from the statement id. A
// name is used only when it is plain letters, digits, dot, dash and underscore: never a path.
const SAFE_CSV_NAME = /^[A-Za-z0-9_-][A-Za-z0-9._-]{0,119}\.csv$/;
const csvFileName = (disposition, statementId) => {
  const named = /filename="([^"]*)"/.exec(typeof disposition === "string" ? disposition : "");
  if (named && SAFE_CSV_NAME.test(named[1])) {
    return named[1];
  }
  const fromId = `${statementId}.csv`;
  return SAFE_CSV_NAME.test(fromId) ? fromId : "partner-settlement.csv";
};

// The whole month as the OMS's CSV file. A file is not the JSON envelope, so this one call reads
// the answer as a blob with the same bearer header GetRequest sends; the token never goes in the
// URL. JSON is accepted too, because a refusal (SETTLEMENT_TOO_LARGE and the rest) is the usual
// error envelope, read back out of the blob and passed through unwrap like every other call.
// `statementId` names the file when the browser cannot read the OMS's file name header.
export const downloadSettlementCsv = async (token, { partnerKey, month }, statementId) => {
  try {
    const response = await window.axios.get(
      `${WARRANTY_PARTNER_SETTLEMENT_API_URL}/csv?${countryQuery({ partnerKey, month })}`,
      {
        headers: { Accept: "text/csv, application/json", Authorization: "Bearer " + token },
        responseType: "blob",
      }
    );
    const headers = response?.headers || {};
    // Only the OMS's CSV is saved: a 200 page from a proxy or a login screen is not a statement.
    if (!String(headers["content-type"] || "").toLowerCase().startsWith("text/csv")) {
      return unwrap({ status: 502 });
    }
    return {
      ok: true,
      blob: response.data,
      filename: csvFileName(headers["content-disposition"], headers["x-statement-id"] || statementId),
      contentSha256:
        typeof headers["x-content-sha256"] === "string" ? headers["x-content-sha256"] : null,
    };
  } catch (error) {
    const data = error?.response?.data;
    let body = {};
    if (data && typeof data.text === "function") {
      try {
        const parsed = JSON.parse(await data.text());
        body = parsed !== null && typeof parsed === "object" ? parsed : {};
      } catch (parseError) {
        // Not the JSON envelope (a proxy's HTML page): the status alone picks the sentence.
        body = {};
      }
    }
    return unwrap({ ...body, status: error?.response?.status });
  }
};

// ---------------------------------------------------------------------------------------------
// Catalogue images (warranty_plans_admin): the component icons and model images the Amigo app
// shows on its Warranty screen. These screens choose the market, so `country` is passed in; the
// body comes from catalogueFields.js (componentPayload, modelImagePayload) and already carries it.
// Each save carries a client request id, as the plans admin writes do; the OMS turns it into the
// Idempotency-Key.
export const listCatalogueComponents = (token, country) =>
  get(`${WARRANTY_CATALOGUE_COMPONENTS_API_URL}?${query({ country })}`, token);

export const saveCatalogueComponent = (token, body, clientRequestId) =>
  post(WARRANTY_CATALOGUE_COMPONENTS_API_URL, { ...body, clientRequestId }, token);

export const listCatalogueModelImages = (token, country) =>
  get(`${WARRANTY_CATALOGUE_MODEL_IMAGES_API_URL}?${query({ country })}`, token);

export const saveCatalogueModelImage = (token, body, clientRequestId) =>
  post(WARRANTY_CATALOGUE_MODEL_IMAGES_API_URL, { ...body, clientRequestId }, token);

// ---------------------------------------------------------------------------------------------
// Theft & roadside to buy (warranty_ops, contract 15 Oct 2026): the paid Assure+ covers waiting for
// the one vendor policy ops buy by hand, and its save. The contract pages with `after` and answers
// `next`; the list answer is turned into { items, nextCursor } so usePagedList reads it as the other
// lists. Oldest first.
export const VENDOR_POLICY_PAGE_SIZE = 50;

export const listPendingVendorPolicies = async (token, cursor) => {
  const result = await get(
    `${WARRANTY_VENDOR_POLICIES_API_URL}/pending?${countryQuery({
      limit: VENDOR_POLICY_PAGE_SIZE,
      after: cursor,
    })}`,
    token
  );
  if (!result.ok) {
    return result;
  }
  return { ...result, data: { items: result.data?.items || [], nextCursor: result.data?.next || null } };
};

// A presigned PUT for the PDF, good for five minutes: { uploadUrl, uploadKey, expiresAt }.
export const createVendorPolicyUploadUrl = (token, subscriptionId, sizeBytes, clientRequestId) =>
  post(
    `${WARRANTY_SUBSCRIPTIONS_API_URL}/${part(subscriptionId)}/vendor-policy/upload-url`,
    { country: WARRANTY_COUNTRY, contentType: "application/pdf", sizeBytes, clientRequestId },
    token
  );

// The PDF goes straight to the presigned link, never through the OMS. fetch and not axios: the
// axios defaults add a header the link was not signed for, and no token or cookie may go to S3.
export const uploadVendorPolicyPdf = async (uploadUrl, file) => {
  try {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/pdf" },
      body: file,
      credentials: "omit",
      referrerPolicy: "no-referrer",
    });
    return { ok: response.ok, httpStatus: response.status };
  } catch (error) {
    // No answer at all (offline, or the bucket refused the browser's CORS check).
    return { ok: false, httpStatus: undefined };
  }
};

// Records the policy: the service marks the theft and roadside covers active and sends the PDF to
// the rider. Answers { subscriptionId, policyNumber, covers, messagesQueued }.
export const recordVendorPolicy = (token, subscriptionId, policyNumber, uploadKey, clientRequestId) =>
  post(
    `${WARRANTY_SUBSCRIPTIONS_API_URL}/${part(subscriptionId)}/vendor-policy`,
    { country: WARRANTY_COUNTRY, policyNumber, uploadKey, clientRequestId },
    token
  );

// The stored policy: { policyNumber, issuedAt, url, expiresAt }, the url good for 15 minutes; a 404
// VENDOR_POLICY_NOT_FOUND when none was added yet.
export const getVendorPolicy = (token, subscriptionId) =>
  get(`${WARRANTY_SUBSCRIPTIONS_API_URL}/${part(subscriptionId)}/vendor-policy?${countryQuery()}`, token);
